import React from 'react';
import { CCol, CModal, CModalBody, CModalHeader, CModalTitle, CRow } from '@coreui/react';
import { ModalCloseButton } from '../views/settings/SettingsButton';
import styled, { css } from 'styled-components';
import { Fonts, Palette } from './utils';
import { Button } from './ui/Button';
import { closeActiveModal } from '../helper/modals';
import { SettingsModalBoldText } from '../views/settings/SettingsModals';
import ModalCloseIcon from '../assets/icons/modal-close-button.svg';
import { Flex } from './ui/Flex';

export const ModalHeader = ({ title, onCloseCallback, v2 = false }) => {
  return (
    <CModalHeader>
      <StyledModalTitle>{title}</StyledModalTitle>
      <div className="ml-auto">
        {v2 ? (
          <ModalCloseButtonV2 onClick={() => closeActiveModal(onCloseCallback)}>
            <img src={ModalCloseIcon} alt="close" />
          </ModalCloseButtonV2>
        ) : (
          <ModalCloseButton onCloseCallback={onCloseCallback} />
        )}
      </div>
    </CModalHeader>
  );
};

export const ModalButtons = ({
  leftButtonText,
  leftButtonClick,
  rightButtonText,
  rightButtonClick,
  rightDisabled,
  disabled
}) => {
  return (
    <CRow>
      <CCol>
        <Button
          className="w-100"
          text={leftButtonText}
          onClick={leftButtonClick}
          disabled={disabled}
        />
      </CCol>
      <CCol>
        <Button
          className="w-100"
          isPrimary
          text={rightButtonText}
          onClick={rightButtonClick}
          disabled={disabled || rightDisabled}
        />
      </CCol>
    </CRow>
  );
};

export const StyledModalTitle = styled(CModalTitle)`
  color: ${Palette.BLACK};
  font-family: ${Fonts.RED_HAT_FONT};
  font-size: 40px;
  font-style: normal;
  font-weight: 800;
  line-height: 56px;
  text-transform: capitalize;
`;

export const StyledModal = ({ onClose, ...props }) => {
  return (
    <StyledCModal
      closeOnBackdrop={false}
      onClose={onClose ? onClose : closeActiveModal}
      {...props}
    />
  );
};

export const StyledCModal = styled(CModal)`
  transition: none;

  .modal .modal-dialog {
    transition: none;
  }
  .modal-content {
    transition: none;
    border-radius: 30px !important;
    background: ${Palette.GHOST_WHITE};
    display: flex;
    flex-direction: column;
    gap: 24px;
    padding: 48px 46px !important;

    position: fixed;
    top: 50%;

    ${({ side }) =>
      side
        ? css`
            right: 0;
            transform: translateY(-50%);
            max-height: 100vh;
            background: ${Palette.WHITE};
            border-radius: 0px !important;
          `
        : css`
            left: 50%;
            transform: translate(-50%, -50%);
            max-height: 85vh;
            border-radius: 30px !important;
          `}
    width: ${({ width }) => width || '548px'};
  }

  .modal-dialog {
    transition: none !important;
    min-width: ${({ width }) => width || '548px'};
    max-height: 85vh;

    ${({ side }) =>
      side &&
      css`
        margin-right: 0;
        height: 100%;
      `}

    .modal-body {
      padding-bottom: 0 !important;
      padding-top: 0 !important;
    }
  }

  ${({ cssOverride }) => cssOverride};
`;

export const ModalCloseButtonV2 = styled.button`
  padding: 0;
  background-color: transparent;
  border: none;

  :focus {
    outline: none;
  }

  :focus-visible {
    outline: 1px solid ${Palette.HAZY_BLUE};
  }
`;

export const ModalButtonsContainer = styled(Flex)`
  border-top: ${({ isAtBottom }) => (isAtBottom ? 'none' : '0.5px solid #D4D4D4')};
  padding: 16px 24px 24px;
  box-shadow: ${({ isAtBottom }) => (isAtBottom ? 'none' : '0px 1px 15px 0px rgba(0, 0, 0, 0.10)')};

  button {
    min-width: 8.875rem;
    padding-top: 0.58rem;
    padding-bottom: 0.58rem;
  }

  ${({ gap }) => gap && `gap: ${gap};`}
  ${({ column }) => column && `flex-direction: column;`}
`;

export const StyledModalBody = styled(CModalBody)`
  display: flex;
  flex-direction: column;
  gap: ${({ gap }) => gap || '24px'};

  overflow-x: hidden;
  overflow-y: scroll;

  padding-right: 3px !important;

  ::-webkit-scrollbar-track {
    display: none;
  }

  ::-webkit-scrollbar {
    width: 10px;
    background-color: none;
  }

  ::-webkit-scrollbar-thumb {
    border-radius: 10px;
    background-color: none;
  }

  :hover {
    ::-webkit-scrollbar-track {
      display: none;
    }

    ::-webkit-scrollbar {
      width: 10px;
      background-color: none;
    }

    ::-webkit-scrollbar-thumb {
      border-radius: 10px;
      background-color: ${Palette.HAZY_BLUE};
    }
  }

  .at-bottom + ${ModalButtonsContainer} {
    box-shadow: none;
  }

  ${({ cssOverride }) => cssOverride};
`;

export const HeaderText = styled(SettingsModalBoldText)`
  margin-bottom: 8px;
  font-size: 20px;
  font-weight: 700;
  line-height: 32px;

  ${({ alt }) =>
    alt &&
    css`
      margin-bottom: 0;
      font-family: ${Fonts.RED_HAT_FONT};
      font-weight: 800;
    `}
`;

export const StyledModalV2 = styled(StyledModal)`
  --input-font-weight: 400;
  --input-font-size: 14px;
  --input-height: 48px;
  --input-padding: 16px;

  .modal-content {
    padding: 0px !important;
    box-shadow: 0px 4px 20px 0px rgba(0, 0, 0, 0.25);
    gap: 0px;
    min-height: 100vh;
  }

  div:has(> label) {
    gap: 4px;
  }

  button[type='submit']:disabled {
    background-color: ${Palette.DISABLED};
    color: #a3a3a3;
    opacity: 1;
    border: none;
  }

  .modal-dialog .modal-content .modal-header {
    padding: 36px 24px 24px;
    border-bottom: 1px solid #d4d4d4;
  }

  .modal-dialog .modal-content .modal-header .modal-title {
    font-size: 25px;
    line-height: 40px;
    font-weight: 800;
  }

  input:not(
      .react-select__input input,
      .ant-picker-input input,
      .ant-select-selection-search-input
    ),
  .ant-select .ant-select-selector {
    padding-left: var(--input-padding) !important;
    height: var(--input-height) !important;
  }

  &:not(.ant-select-customize-input)
    .ant-select-selector
    .ant-select-selection-search
    .ant-select-selection-search-input {
    height: var(--input-height);
    padding-left: 5px;
    font-size: var(--input-font-size);
    font-weight: var(--input-font-weight);
  }

  input,
  .ant-select .ant-select-selector {
    border-radius: 8px !important;
  }

  .react-select__control,
  .react-select__value-container {
    height: var(--input-height) !important;
  }

  .react-select__control .react-select__input input {
    font-size: var(--input-font-size) !important;
  }

  .ant-picker {
    height: var(--input-height);
    padding-left: var(--input-padding);
  }

  input,
  textarea,
  .ant-select .ant-select-selector .ant-select-selection-item,
  .ant-select .ant-select-selector .ant-select-selection-placeholder,
  .react-select__control,
  .react-select__single-value {
    font-weight: var(--input-font-weight);
    font-size: var(--input-font-size);
  }

  .react-select__option {
    font-size: var(--input-font-size);
  }

  .ant-picker-input input {
    font-size: var(--input-font-size) !important;
    font-weight: var(--input-font-weight) !important;
  }

  input::placeholder,
  .ant-select .ant-select-selector .ant-select-selection-item,
  .ant-select .ant-select-selector .ant-select-selection-placeholder {
    color: ${Palette.CONTENT_TERTIARY};
  }

  .react-select__control {
    padding-left: var(--input-padding);
  }

  textarea {
    padding-left: var(--input-padding);
  }
`;

export const ModalBodyContent = styled.div`
  padding: 0px 11.5px 20px 24px;
`;

export const SectionSubtitle = styled.p`
  color: ${Palette.CONTENT_TERTIARY};
`;

export const SectionContainer = styled.div`
  :not(:first-of-type) {
    padding-top: 24px;
    border-top: 1px solid #d4d4d4;
  }

  :not(:last-of-type) {
    padding-bottom: 24px;
  }
`;